import {
  Constraints,
  DynamicPage,
  DynamicPageVersion,
  MarketingApproach,
  SectionData,
  Website,
  WebsiteVariantTest,
} from '@betterfunnel/types';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  setDoc,
  startAfter,
  Timestamp,
  UpdateData,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';

export type CreatableWebsiteSectionType =
  | 'hero'
  | 'logos'
  | 'cta'
  | 'features'
  | 'testimonials'
  | 'faqs'
  | 'contactUs'
  | 'calendly'
  | 'footer';

export const dynamicPagesColRef = (accountId: string) => {
  return collection(db, 'accounts', accountId, 'campaigns').withConverter(
    emptyConverter<DynamicPage>(),
  );
};

export const dynamicPagesQuery = (accountId: string) => {
  return query(
    dynamicPagesColRef(accountId),
    where('status', 'in', ['active', 'disabled']),
    orderBy('createdAt', 'desc'),
  );
};

export const createNewDynamicPageVariant = async (
  accountId: string,
  dynamicPageId: string,
  name: string,
  description: string | undefined,
  approach: MarketingApproach,
  constraints: Constraints,
  prompt: string | undefined,
): Promise<{ section: SectionData }> => {
  const fn = httpsCallable<unknown, { section: SectionData }>(
    functions,
    'accounts-campaigns-createNewVariant',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    name,
    description,
    approach,
    constraints,
    ...(prompt ? { prompt } : {}),
  });

  return res.data;
};

export const createNewDynamicPageVariantTest = async (
  _: string,
  __: string,
  ___: string,
  ____?: WebsiteVariantTest,
): Promise<{ ok: boolean }> => {
  throw new Error('Not implemented');
  // const fn = httpsCallable<unknown, { ok: boolean }>(
  //   functions,
  //   'accounts-campaigns-createNewVariantTest',
  // );

  // const res = await fn({
  //   accountId,
  //   campaignId: dynamicPageId,
  //   variantId,
  //   test,
  // });

  // return res.data;
};

export const publishWebsite = async (
  accountId: string,
  dynamicPageId: string,
  websiteId: string,
): Promise<any> => {
  const fn = httpsCallable(functions, 'accounts-websites-publishWebsite');

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    websiteId,
  });

  return res.data;
};

export const updateWebsiteStatus = async (
  accountId: string,
  dynamicPageId: string,
  websiteId: string,
  status: 'active' | 'disabled',
): Promise<any> => {
  const websiteRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
    'websites',
    websiteId,
  );

  return updateDoc(websiteRef, { status });
};

export const suggestCampaignForAd = async (
  accountId: string,
  adId: string,
): Promise<{
  campaign: Partial<DynamicPage>;
  variants: Partial<Website>[];
}> => {
  const fn = httpsCallable<
    any,
    { campaign: Partial<DynamicPage>; variants: Partial<Website>[] }
  >(functions, 'accounts-campaigns-suggestCampaignForAd', { timeout: 600000 });

  const res = await fn({
    accountId,
    adId,
  });

  return res.data;
};

export const duplicateDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
): Promise<{ campaignId: string }> => {
  const fn = httpsCallable<any, { campaignId: string }>(
    functions,
    'accounts-campaigns-duplicateCampaign',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
  });

  return res.data;
};

export const updateDynamicPageHasPendingPublishChanges = async (
  accountId: string,
  dynamicPageId: string,
  hasChanges: boolean,
): Promise<void> => {
  const dynamicPageRef = doc(dynamicPagesColRef(accountId), dynamicPageId);

  return setDoc(
    dynamicPageRef,
    { hasChangesToPublish: hasChanges },
    { merge: true },
  );
};

export const getPrevDynamicPageVersion = async (
  accountId: string,
  dynamicPageId: string,
): Promise<QueryDocumentSnapshot<DynamicPageVersion>[]> => {
  const dynamicPageDocRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
  );

  const dynamicPageVersionsColRef = collection(
    dynamicPageDocRef,
    'versions',
  ).withConverter(emptyConverter<DynamicPageVersion>());

  return getDocs(
    query(
      dynamicPageVersionsColRef,
      where('type', '==', 'dynamicPageVersion'),
      orderBy('createdAt', 'desc'),
      limit(1),
    ),
  ).then((querySnapshot) => querySnapshot.docs);
};

export const createDynamicPageVersion = async (
  accountId: string,
  dynamicPageId: string,
  dynamicPageVersion: DynamicPageVersion,
): Promise<{ id: string }> => {
  const dynamicPageDocRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
  );

  const dynamicPageVersionsColRef = collection(
    dynamicPageDocRef,
    'versions',
  ).withConverter(emptyConverter<DynamicPageVersion>());

  const res = await addDoc(dynamicPageVersionsColRef, dynamicPageVersion).then(
    (docRef) => ({ id: docRef.id }),
  );

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, false);

  return res;
};

export const getDynamicPageVersions = async (
  accountId: string,
  dynamicPageId: string,
  _limit: number,
  last?: QueryDocumentSnapshot<DynamicPageVersion>,
): Promise<QueryDocumentSnapshot<DynamicPageVersion>[]> => {
  const dynamicPageDocRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
  );

  const dynamicPageVersionsColRef = collection(
    dynamicPageDocRef,
    'versions',
  ).withConverter(emptyConverter<DynamicPageVersion>());

  return getDocs(
    query(
      dynamicPageVersionsColRef,
      where('type', '==', 'dynamicPageVersion'),
      orderBy('createdAt', 'desc'),
      ...(last ? [startAfter(last)] : []),
      limit(_limit),
    ),
  ).then((querySnapshot) => querySnapshot.docs);
};

export const updateDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
  update: UpdateData<
    Pick<
      DynamicPage,
      | 'name'
      | 'description'
      | 'config'
      | 'brand'
      | 'status'
      | 'tags'
      | 'connections'
    >
  >,
  mergeFields?: string[],
): Promise<any> => {
  const dynamicPageDocRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
  );

  const res = await setDoc(dynamicPageDocRef, update, {
    merge: true,
    mergeFields,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res;
};

export const updateDynamicPageMetadata = async (
  accountId: string,
  dynamicPageId: string,
  update: { path?: string; alternatePathes?: string[] },
): Promise<any> => {
  const fn = httpsCallable<any, any>(
    functions,
    'accounts-campaigns-updateCampaign',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    update,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const deleteDynamicPage = (accountId: string, dynamicPageId: string) => {
  return updateDynamicPage(accountId, dynamicPageId, { status: 'deleted' });
};

export const getAllDynamicPagesLinks = async (
  accountId: string,
  dynamicPageIds: string[],
): Promise<string[]> => {
  const fn = httpsCallable<any, any>(
    functions,
    'accounts-campaigns-linkReplacer-getAllLinks',
    { timeout: 600000 },
  );

  const res = await fn({
    accountId,
    campaignIds: dynamicPageIds,
  });

  return res.data;
};

export const createPreviewDynamicPageVersion = async (
  accountId: string,
  dynamicPageId: string,
): Promise<DynamicPageVersion> => {
  const fn = httpsCallable<any, DynamicPageVersion>(
    functions,
    'accounts-campaigns-versions-createPreviewDpVersion',
  );

  const res = await fn({
    accountId,
    dynamicPageId,
  });

  return { ...res.data, createdAt: Timestamp.now() };
};

export const replaceAllDynamicPagesLinks = async (
  accountId: string,
  dynamicPageIds: string[],
  mappings: { link: string; toLink: string }[],
): Promise<any> => {
  const fn = httpsCallable<any, any>(
    functions,
    'accounts-campaigns-linkReplacer-replaceLinks',
    { timeout: 600000 },
  );

  const res = await fn({
    accountId,
    campaignIds: dynamicPageIds,
    mappings,
  });

  return res.data;
};
